/**
 * The sections of Settings, their words, and which reader gets which.
 *
 * ONE LIST, AND THE NAV, THE PANE HEAD AND THE URL ALL READ IT. The tab strip this replaced
 * wrote its labels in `Settings.tsx`, its agent tabs in `AgentSections.tsx` and its operator
 * cards behind a role check in the middle of a form - three places that had to agree about
 * what a custom agent does not have. Here a section says once what group it is in, whether
 * it is our team's, and whether it only exists for a hosted agent.
 *
 * THE WORDS ARE THE PROTOTYPE'S, MOVED TO THE PRODUCT'S TERMS. "Settings v2" in Claude
 * Design wrote "clients don't see this" and "every business in this account"; the terms
 * table (`docs/terms.md`) says organization, member and "Our team only", so that is what is
 * here. No em dashes, which the prototype's own project notes also ask for.
 *
 * THE URL KEEPS `?tab=`, AND THE OLD NAMES STILL WORK. The Shell's Team link, the overview's
 * "set your real hours" link and every bookmark made before this page had a nav say
 * `?tab=business` or `?tab=agent`. They are aliases rather than redirects - normalised on
 * read, never rewritten - which is the rule `useTab` kept for the same reason: a redirect is
 * a render, a write and a second render, and a loop waiting for a dependency array.
 */

export type SectionId =
  | "details"
  | "hours"
  | "greeting"
  | "instructions"
  | "tools"
  | "knowledge"
  | "tracking"
  | "team"
  | "languages"
  | "voice"
  | "agenttype"
  | "demopage";

export type GroupId = "business" | "agent" | "calls" | "people" | "ours";

export interface SectionInfo {
  /** The word in the nav. */
  label: string;
  /** The pane's heading, which can say more than the nav has room for. */
  title: string;
  /** One sentence under the heading, always. The rule `Section` kept, carried over. */
  description: string;
  group: GroupId;
  /** Our team's: drawn for an internal reader only, under the accent group title. */
  internal?: boolean;
  /** Only a hosted agent has it. A custom agent's instructions and tools are its code. */
  hostedOnly?: boolean;
}

export const SECTIONS: Record<SectionId, SectionInfo> = {
  details: {
    label: "Details",
    title: "Business details",
    description: "Who you are, where you are, and where we send your calls.",
    group: "business",
  },
  hours: {
    label: "Hours & recording",
    title: "Hours & recording",
    description:
      "Your locations, when each is open, and days you're closed. Your agent knows if it's answering inside or outside these hours.",
    group: "business",
  },
  greeting: {
    label: "Greeting",
    title: "Greeting",
    description: "The first sentence your agent says when it picks up.",
    group: "agent",
  },
  instructions: {
    label: "Instructions",
    title: "Instructions",
    description:
      "Brief your agent like a new receptionist: who it is, how it talks, what you do and don't do.",
    group: "agent",
    hostedOnly: true,
  },
  tools: {
    label: "Tools",
    title: "Tools",
    description:
      "Actions your agent can take during a call, like taking a message or looking something up in another system.",
    group: "agent",
    hostedOnly: true,
  },
  knowledge: {
    label: "Knowledge",
    title: "Knowledge",
    description: "Documents and links your agent can answer from.",
    group: "agent",
  },
  tracking: {
    label: "Call tracking",
    title: "Call tracking",
    description:
      "After each call we note why they rang and how they sounded. You choose the reasons.",
    group: "calls",
  },
  team: {
    label: "Team",
    title: "Team",
    description: "People who can sign in. Everyone here can open every agent in this organization.",
    group: "people",
  },
  languages: {
    label: "Languages",
    title: "Languages",
    description: "The language calls start in, and others the agent can switch to.",
    group: "ours",
    internal: true,
  },
  voice: {
    label: "Voice",
    title: "Voice",
    description: "How the agent sounds.",
    group: "ours",
    internal: true,
  },
  agenttype: {
    label: "Agent type",
    title: "Agent type",
    description: "Decides whether Instructions, Tools and Try it appear for this agent.",
    group: "ours",
    internal: true,
  },
  demopage: {
    label: "Demo page",
    title: "Demo page",
    description: "A link to hand to somebody so they can talk to this agent in their browser.",
    group: "ours",
    internal: true,
    hostedOnly: true,
  },
};

/** The nav's groups, in order. Upper case is the nav's styling, not the words'. */
export const GROUPS: Array<{ id: GroupId; title: string; sections: SectionId[] }> = [
  { id: "business", title: "Business", sections: ["details", "hours"] },
  { id: "agent", title: "Agent", sections: ["greeting", "instructions", "tools", "knowledge"] },
  { id: "calls", title: "Calls", sections: ["tracking"] },
  { id: "people", title: "People", sections: ["team"] },
  { id: "ours", title: "Our team only", sections: ["languages", "voice", "agenttype", "demopage"] },
];

/** The canonical section, and the page's address with no query. */
export const FIRST: SectionId = "details";

/** What the tab strip called things, so a link made before the nav still lands. */
const ALIASES: Record<string, SectionId> = { business: "details", agent: "hours" };

export interface Reader {
  runtime: "custom" | "hosted";
  internal: boolean;
}

/** Whether this reader, on this agent, has the section at all. */
export function available(id: SectionId, reader: Reader): boolean {
  const info = SECTIONS[id];
  if (info.internal && !reader.internal) return false;
  if (info.hostedOnly && reader.runtime !== "hosted") return false;
  return true;
}

/** The nav as this reader sees it: empty groups dropped, which is the whole of the team-only rule. */
export function groupsFor(reader: Reader): Array<{ id: GroupId; title: string; sections: SectionId[] }> {
  return GROUPS.map((group) => ({
    ...group,
    sections: group.sections.filter((id) => available(id, reader)),
  })).filter((group) => group.sections.length > 0);
}

/**
 * The section a `?tab=` value means for this reader.
 *
 * A NAME THIS READER DOES NOT HAVE IS NOT AN ERROR. `?tab=tools` on a custom agent, or
 * `?tab=voice` opened by a member from a link an operator pasted, lands on Details - the
 * same answer `?tab=nonsense` gets, and for the same reason: an empty pane is worse.
 */
export function resolveSection(raw: string | null, reader: Reader): SectionId {
  if (!raw) return FIRST;
  const id = (ALIASES[raw] ?? raw) as SectionId;
  return id in SECTIONS && available(id, reader) ? id : FIRST;
}
